import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../services/api';

export default function TwoFactorSetup({ tempToken, onSuccess }) {
  const [qrcodeDataUrl, setQrcodeDataUrl] = useState(null);
  const [secretBase32, setSecretBase32] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingQr, setLoadingQr] = useState(true);
  const [secretVisible, setSecretVisible] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    auth.setup2fa(tempToken)
      .then(res => {
        setQrcodeDataUrl(res.data.qrcode_data_url);
        setSecretBase32(res.data.secret_base32);
        setLoadingQr(false);
        setTimeout(() => inputRef.current?.focus(), 100);
      })
      .catch(() => {
        setError('Erreur lors de la génération du QR code. Veuillez vous reconnecter.');
        setLoadingQr(false);
      });
  }, [tempToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.replace(/\s/g, '').length !== 6) {
      setError('Le code doit contenir 6 chiffres');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await auth.enable2fa(tempToken, code.replace(/\s/g, ''));
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      onSuccess(res.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Code incorrect');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const formatSecret = (s) => s.match(/.{1,4}/g)?.join(' ') || s;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '24px',
    }}>
      <div style={{
        background: 'var(--bg-surface)',
        borderRadius: '16px',
        padding: '40px',
        width: '100%',
        maxWidth: '440px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* En-tête */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🛡️</div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 6px' }}>
            Sécurisez votre compte
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>
            Configuration de la vérification en deux étapes
          </p>
        </div>

        {/* Étapes */}
        <div style={{ marginBottom: '24px' }}>
          {[
            { n: '1', text: 'Installez Microsoft Authenticator (ou Google Authenticator) sur votre téléphone' },
            { n: '2', text: 'Scannez le QR code ci-dessous avec l\'application' },
            { n: '3', text: 'Entrez le code à 6 chiffres affiché pour confirmer' },
          ].map(step => (
            <div key={step.n} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{
                minWidth: '26px', height: '26px', borderRadius: '50%',
                background: '#C9A84C', color: 'var(--text-inverse)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: '700',
              }}>{step.n}</div>
              <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: '1.5', paddingTop: '3px' }}>
                {step.text}
              </p>
            </div>
          ))}
        </div>

        {/* QR Code */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          {loadingQr ? (
            <div style={{ padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>
              Génération du QR code...
            </div>
          ) : qrcodeDataUrl ? (
            <>
              <img
                src={qrcodeDataUrl}
                alt="QR code 2FA"
                style={{ width: '200px', height: '200px', border: '4px solid var(--bg-muted)', borderRadius: '8px' }}
              />
              {/* Secret manuel */}
              <div style={{ marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={() => setSecretVisible(v => !v)}
                  style={{
                    background: 'none', border: 'none', color: 'var(--text-muted)',
                    fontSize: '12px', cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  {secretVisible ? 'Masquer' : 'Saisie manuelle (impossible de scanner ?)'}
                </button>
                {secretVisible && (
                  <div style={{
                    marginTop: '8px', padding: '10px', background: 'var(--bg-muted)',
                    borderRadius: '8px', border: '1px solid var(--border-default)',
                    fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-primary)',
                    letterSpacing: '2px', wordBreak: 'break-all',
                  }}>
                    {formatSecret(secretBase32)}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--color-error)', fontSize: '13px' }}>{error}</div>
          )}
        </div>

        {/* Formulaire */}
        {!loadingQr && qrcodeDataUrl && (
          <form onSubmit={handleSubmit}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
              Code de vérification
            </label>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9 ]*"
              maxLength={7}
              value={code}
              onChange={e => {
                const v = e.target.value.replace(/[^0-9]/g, '');
                setCode(v);
                setError('');
              }}
              placeholder="000000"
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '28px',
                fontWeight: '700',
                textAlign: 'center',
                letterSpacing: '10px',
                border: `2px solid ${error ? 'var(--color-error)' : code.length === 6 ? 'var(--color-success)' : 'var(--border-default)'}`,
                borderRadius: '10px',
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: '8px',
                color: '#1a1a2e',
              }}
              autoComplete="one-time-code"
            />

            {error && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: '8px', padding: '10px 14px',
                color: 'var(--color-error)', fontSize: '13px', marginBottom: '14px',
              }}>
                ⚠️ {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || code.length !== 6}
              style={{
                width: '100%',
                padding: '14px',
                background: code.length === 6 ? 'linear-gradient(135deg, #C9A84C, #a07830)' : 'var(--border-default)',
                color: code.length === 6 ? 'var(--text-inverse)' : 'var(--text-muted)',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '700',
                cursor: code.length === 6 ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
              }}
            >
              {loading ? 'Vérification...' : '✅ Activer la 2FA et accéder au CRM'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
